// src/components/settings/LanguageSettingsCard.jsx
'use client';

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { GlobeAltIcon } from '@heroicons/react/24/outline';

import { useAuth } from '@/context/AuthContext';
import { setAppLanguage } from '@/i18n';

/**
 * A dropdown that lets the user pick the UI + AI-generated message language
 * and persists the choice to their Firestore profile. i18next is switched
 * immediately for instant feedback.
 *
 * Drop this into ProfileSettings (or anywhere else a language preference
 * belongs). No props required — it reads the current language from the
 * authenticated user profile and falls back to whatever i18next has
 * already detected.
 */
export default function LanguageSettingsCard() {
  const { t, i18n } = useTranslation();
  const { userProfile, updateUserProfile } = useAuth();

  const currentLang = useMemo(() => {
    const raw = userProfile?.language || userProfile?.locale || i18n.language || 'en';
    return String(raw).toLowerCase().startsWith('es') ? 'es' : 'en';
  }, [userProfile?.language, userProfile?.locale, i18n.language]);

  const [saving, setSaving] = useState(false);

  const handleChange = async (e) => {
    const next = e.target.value === 'es' ? 'es' : 'en';
    if (next === currentLang) return;

    // Apply instantly so the UI flips regardless of network speed
    setAppLanguage(next);

    if (!userProfile?.id || !updateUserProfile) {
      // Not signed in (rare in this view) — localStorage will still persist
      return;
    }

    try {
      setSaving(true);
      await updateUserProfile({ language: next });
      toast.success(t('settings.language.toastSaved'));
    } catch (err) {
      console.error('[LanguageSettingsCard] failed to save language', err);
      toast.error(err?.message || 'Could not save your language preference.');
      // Roll back the UI if the write failed
      setAppLanguage(currentLang);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 shadow-sm">
      <div className="border-b border-zinc-100 dark:border-zinc-800 px-6 py-4 flex items-center gap-3">
        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-blue-600 dark:text-blue-400">
          <GlobeAltIcon className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-base font-bold text-zinc-900 dark:text-white">
            {t('settings.language.title')}
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            {t('settings.language.description')}
          </p>
        </div>
      </div>

      <div className="p-6">
        <label
          htmlFor="scrimly-language-select"
          className="block text-sm font-medium text-zinc-700 dark:text-zinc-200 mb-2"
        >
          {t('settings.language.label')}
        </label>
        <select
          id="scrimly-language-select"
          value={currentLang}
          onChange={handleChange}
          disabled={saving}
          className="block w-full max-w-xs rounded-lg border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-800 px-3 py-2 text-sm text-zinc-900 dark:text-zinc-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-60"
        >
          <option value="en">{t('settings.language.options.en')}</option>
          <option value="es">{t('settings.language.options.es')}</option>
        </select>
      </div>
    </div>
  );
}
